import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useSalir } from '../api/hooks'
import { useSesion } from '../auth/sesion'
import { useUnidadesCarrito } from '../carrito/estado'
import { Asistente } from './Asistente'
import { Campana } from './Campana'
import { Logotipo } from './Logotipo'
import {
  IconoBalanza,
  IconoCarrito,
  IconoCatalogo,
  IconoEscudo,
  IconoFabrica,
  IconoOrden,
  IconoPanel,
} from './icons'

function enlaceClase({ isActive }: { isActive: boolean }) {
  return [
    'inline-flex items-center gap-1.5 px-2.5 py-2 text-[13px] border-b-2 -mb-px whitespace-nowrap',
    isActive
      ? 'border-senal-500 text-acero-950 font-semibold'
      : 'border-transparent text-acero-600 hover:text-acero-900 hover:border-acero-300',
  ].join(' ')
}

/**
 * Estructura de toda pantalla con sesión: franja oscura de marca, línea de
 * estado de sesión, pestañas y contenido.
 *
 * A diferencia de la v1 no hay modo demostración ni selector de rol: la
 * demostración son cuentas reales, así que la navegación sigue siempre el rol
 * real de quien entró. Ese selector era la fuente del peor malentendido posible
 * —creer que se está viendo el sistema real cuando se veían datos de ejemplo— y
 * ya no existe nada que seleccionar.
 */
export function Layout() {
  const { yo, esEquipo, esAdmin, puedeComprar, suspendida, administraEmpresa } = useSesion()
  const salir = useSalir()
  const navigate = useNavigate()
  const unidades = useUnidadesCarrito()

  const esProveedor = yo?.rol === 'proveedor'

  function cerrarSesion() {
    salir.mutate(undefined, {
      // Se navega en `onSettled` y no en `onSuccess`: si el servidor falla, la
      // caché local ya se vació y dejarla en una pantalla privada sería peor.
      onSettled: () => navigate('/entrar', { replace: true }),
    })
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Grafito de marca (#26292E), no el acero-950 de la interfaz: la franja
          superior es donde la marca se reconoce y le corresponde su color. */}
      <header className="text-acero-100" style={{ backgroundColor: 'var(--color-grafito)' }}>
        <div className="mx-auto max-w-[1400px] px-3 sm:px-4">
          <div className="flex items-center gap-3 py-2.5 flex-wrap">
            {/*
              El logotipo lleva a cada uno a SU pantalla de inicio. Para un
              proveedor la portada es la vitrina, de la que ahora se lo
              redirige: apuntarlo a `/` lo haría rebotar y dejaría el salto
              registrado en el historial.
            */}
            <NavLink to={esProveedor ? '/proveedor' : '/'} className="flex items-center">
              <Logotipo sobre="oscuro" />
            </NavLink>


            <div className="ml-auto flex items-center gap-2 flex-wrap">
              {/*
                El carrito se muestra a quien puede comprar, y TAMBIÉN a quien
                todavía no entró pero ya eligió algo.

                Sin esa segunda mitad la corrección anterior queda coja: la
                elección de un visitante anónimo sí se guarda, pero él no ve
                ninguna señal de que ocurrió ni tiene por dónde volver a ella.
                El enlace apunta a `/carrito`, que está detrás de `Privada`: lo
                lleva a entrar y lo devuelve a su carrito, ya con lo suyo
                dentro.
              */}
              {(puedeComprar || (!yo && unidades > 0)) && (
                <NavLink
                  to="/carrito"
                  className="relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm
                    bg-acero-800 hover:bg-acero-700 text-[12.5px]"
                >
                  <IconoCarrito size={15} />
                  Carrito
                  {/*
                    El contador sale del carrito local, no de una consulta: por
                    eso puede pintarse sin pedir nada y sin un badge en cero
                    esperando la respuesta. En cero no se dibuja: un "0" fijo
                    junto al icono es ruido, no información.
                  */}
                  {unidades > 0 && (
                    <span
                      className="num text-[11px] font-semibold bg-senal-500 text-acero-950 rounded-sm px-1 leading-[16px]"
                      aria-label={`${unidades} unidades en el carrito`}
                    >
                      {unidades}
                    </span>
                  )}
                </NavLink>
              )}

              {/*
                La campana va junto al nombre y no en la barra de pestañas: es un
                aviso, no un destino, y su lugar es el de los controles de la
                sesión. La ven las tres cuentas —equipo, cliente y proveedor— y
                cada una recibe lo suyo, porque el servidor filtra con el mismo
                alcance que rige el resto de la API.

                Solo con sesión: sin ella no hay órdenes de las que avisar.
              */}
              {yo && <Campana />}

              {yo ? (
                <div className="flex items-center gap-1.5 pl-2 border-l border-acero-700">
                  <span className="hidden sm:block text-[11.5px] text-acero-300 leading-tight text-right">
                    {yo.nombre}
                    <span className="block text-[10px] text-acero-500 uppercase tracking-[0.08em]">
                      {yo.rol}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={cerrarSesion}
                    disabled={salir.isPending}
                    className="text-[11.5px] text-acero-400 hover:text-acero-100 px-2 py-1.5 disabled:opacity-50"
                    title={`Cerrar la sesión de ${yo.email}`}
                  >
                    {salir.isPending ? 'Saliendo…' : 'Salir'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 pl-2 border-l border-acero-700">
                  <NavLink
                    to="/entrar"
                    className="text-[12.5px] text-acero-300 hover:text-white px-2 py-1.5"
                  >
                    Entrar
                  </NavLink>
                  <NavLink to="/registro" className="btn btn-accion btn-xs">
                    Crear cuenta
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-acero-900/60 border-t border-acero-800">
          <div className="mx-auto max-w-[1400px] px-3 sm:px-4 py-1 text-[11.5px] text-acero-400 flex items-center gap-2 flex-wrap">
            <span className="text-acero-500">Sesión:</span>
            {suspendida ? (
              <span className="text-senal-300">
                Su cuenta está suspendida · comuníquese con un administrador
              </span>
            ) : yo ? (
              <span className="text-acero-200">
                {yo.email}
                {yo.organizacion && ` · ${yo.organizacion}`}
                {esEquipo && ' · Administración interna · Compranorte'}
                {yo.rol === 'pendiente' && ' · sin permisos asignados todavía'}
                {!yo.correoVerificado && ' · correo sin confirmar'}
              </span>
            ) : (
              <span className="text-acero-300">Visitante · está viendo el catálogo público</span>
            )}
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-acero-200 sticky top-0 z-20">
        <div className="mx-auto max-w-[1400px] px-3 sm:px-4 flex items-center gap-1 overflow-x-auto">
          {/*
            El primer enlace es el que la persona lee como "su" pantalla. Para
            un proveedor eso es su panel, no la vitrina: dejarle el catálogo
            adelante hace que la plataforma se sienta de cliente aunque tenga
            todo lo suyo a un clic de distancia.
          */}
          {/*
            LA BARRA DEL PROVEEDOR Y LA DEL EQUIPO NO SON LA MISMA, aunque los
            dos abran el mismo panel.

            Para el PROVEEDOR, sus cuatro trabajos son toda su plataforma y cada
            uno merece su entrada: publicar, cargar catálogo, atender las
            órdenes que llegan y ver cuándo se le paga. Antes eran pestañas
            escondidas dentro de «Mis productos».

            Para el EQUIPO no: el panel de proveedor es UNA de sus herramientas
            —opera por cuenta de un proveedor eligiéndolo en el desplegable— y
            compite en la barra con el panel de administración, usuarios,
            catálogo y órdenes. Cuatro entradas de proveedor ahí adentro
            desplazan lo que el equipo usa todos los días. Conserva las dos de
            siempre y llega a las otras dos por la tira de pestañas del panel,
            que es donde las tenía.

            `end` en «Mis productos» porque `/proveedor` es prefijo de las otras
            rutas: sin él quedarían todas marcadas como activas a la vez.
          */}
          {(esProveedor || esEquipo) && (
            <>
              <NavLink to="/proveedor" end className={enlaceClase}>
                <IconoFabrica size={15} /> Mis productos
              </NavLink>
              <NavLink to="/proveedor/catalogo" className={enlaceClase}>
                <IconoCatalogo size={15} /> Cargar catálogo
              </NavLink>
            </>
          )}
          {esProveedor && (
            <>
              <NavLink to="/proveedor/ordenes" className={enlaceClase}>
                <IconoOrden size={15} /> Órdenes recibidas
              </NavLink>
              <NavLink to="/proveedor/pagos" className={enlaceClase}>
                <IconoBalanza size={15} /> Cuándo me pagan
              </NavLink>
            </>
          )}
          {esEquipo && (
            <NavLink to="/admin" className={enlaceClase}>
              <IconoPanel size={15} /> Panel admin
            </NavLink>
          )}
          {esAdmin && (
            <NavLink to="/admin/usuarios" className={enlaceClase}>
              <IconoEscudo size={15} /> Usuarios
            </NavLink>
          )}
          {/*
            La vitrina NO es para el proveedor.

            Él entra a publicar y a despachar, no a comprar, y en el catálogo su
            propio producto aparece con el PRECIO DE VENTA: de esa cifra y de su
            costo el margen se despeja restando. El enlace se quita y la ruta
            también lo redirige, para que escribir la URL a mano no sea una
            puerta lateral.
          */}
          {!esProveedor && (
            <NavLink to="/catalogo" className={enlaceClase}>
              <IconoCatalogo size={15} /> Catálogo
            </NavLink>
          )}
          {/*
            EL CARRITO ES DEL CLIENTE, NO DE LA ADMINISTRACIÓN.

            `puedeComprar` incluye al equipo para que pueda emitir a nombre de un
            cliente, pero en la barra del equipo el carrito era ruido: nadie
            administra comprando, y la pestaña competía por atención con el panel,
            que es donde de verdad se trabaja.

            La ruta sigue existiendo y el permiso también: quien necesite emitir
            a nombre de un cliente puede ir a /carrito. Lo que se quita es el
            enlace permanente, no la capacidad.
          */}
          {puedeComprar && !esEquipo && (
            <NavLink to="/carrito" className={enlaceClase}>
              <IconoCarrito size={15} /> Carrito
            </NavLink>
          )}
          {puedeComprar && (
            <NavLink to="/mis-ordenes" className={enlaceClase}>
              <IconoOrden size={15} /> {esEquipo ? 'Órdenes' : 'Mis órdenes'}
            </NavLink>
          )}
          {/*
            La entrada a la administración de cuentas de la propia empresa.

            La ve el administrador de un cliente o de un proveedor, y NO el
            equipo de Compra Norte: el equipo tiene su propia pantalla en
            «Usuarios», que además puede mover una cuenta de una empresa a otra.
            Dos poderes distintos, dos entradas distintas.

            Va al final de la barra a propósito: repartir accesos se hace de vez
            en cuando, y adelante competiría con lo que sí se usa todos los días.
          */}
          {administraEmpresa && (
            <NavLink to="/empresa/cuentas" className={enlaceClase}>
              <IconoEscudo size={15} /> Cuentas
            </NavLink>
          )}
        </div>
      </nav>

      <main className="flex-1 mx-auto max-w-[1400px] w-full px-3 sm:px-4 py-4">
        <Outlet />
      </main>

      {/*
        El asistente va en el armazón y no en el catálogo: quien no encuentra
        algo no siempre está en la vitrina —puede venir de la portada o de una
        ficha— y hacerle volver a un sitio concreto para pedir ayuda es
        exactamente la fricción que el asistente existe para quitar.

        Él mismo decide si corresponde mostrarse: un proveedor no compra, así que
        no lo ve.
      */}
      <Asistente />

      <footer className="border-t border-acero-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-3 sm:px-4 py-3 text-[11.5px] text-acero-500 flex flex-wrap gap-x-4 gap-y-1 justify-between">
          <span>
            Compranorte · Un solo RUT, una sola orden, una sola factura, una sola entrega a faena.
          </span>
          <span>Sin pasarela de pago y sin integración con el SII</span>
        </div>
      </footer>
    </div>
  )
}
